import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Eye, PackageCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, newIdempotencyKey } from "../api/client";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { PageHeader } from "../components/PageHeader";
import { formatDate, formatNumber } from "../lib/format";

type Batch = {
  id: string;
  batch_number: string;
  scheduled_date: string;
  quantity: number;
  status: string;
  version: number;
  agent_name: string;
  scope_name: string;
  delivered: number;
  remaining: number;
  no_show: number;
};

type DeliveryResult = {
  batchNumber: string;
  deliveredCount: number;
  alreadyDeliveredCount: number;
  noShowCount: number;
};

export function DeliveryPage() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<Batch | null>(null);
  const [result, setResult] = useState<DeliveryResult | null>(null);
  const batches = useQuery({
    queryKey: ["deliverable-batches"],
    queryFn: async () =>
      (await api<Batch[]>("/batches?limit=100")).data.filter((batch) =>
        ["ready", "in_progress"].includes(batch.status)
      )
  });
  const deliver = useMutation({
    mutationFn: (batch: Batch) =>
      api<DeliveryResult>(`/batches/${batch.id}/deliver`, {
        method: "POST",
        body: { expectedVersion: batch.version },
        idempotencyKey: newIdempotencyKey()
      }),
    onSuccess: async (response) => {
      setResult(response.data);
      setConfirming(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["deliverable-batches"] }),
        queryClient.invalidateQueries({ queryKey: ["batches"] })
      ]);
    }
  });

  return (
    <div className="page">
      <PageHeader
        title="تسليم الدفعات"
        description="التسليم إجراء واحد للدفعة كاملة؛ يسجل الخادم المستفيدين ذريًا ويستثني حالات عدم الحضور."
      />
      {result ? (
        <div className="success-panel" role="status">
          <CheckCircle2 aria-hidden="true" />
          <p>
            اكتملت الدفعة {result.batchNumber}: سُلّمت {formatNumber(result.deliveredCount)} أسرة الآن، وكان{" "}
            {formatNumber(result.alreadyDeliveredCount)} مسجلًا سابقًا، واستُثنيت{" "}
            {formatNumber(result.noShowCount)}
            حالة عدم حضور.
          </p>
        </div>
      ) : null}
      {confirming ? (
        <section className="content-card confirmation-panel" aria-labelledby="batch-delivery-confirm-title">
          <div>
            <h2 id="batch-delivery-confirm-title">تأكيد تسليم الدفعة {confirming.batch_number}</h2>
            <p>
              سيسجل النظام تسليم {formatNumber(confirming.remaining)} تخصيصًا قائمًا دفعة واحدة، ويغلق الدفعة.
              حالات عدم الحضور المسجلة ({formatNumber(confirming.no_show)}) لن تُحسب كتسليم.
            </p>
          </div>
          {deliver.isError ? (
            <div className="inline-alert" role="alert">
              {formatApiError(deliver.error)}
            </div>
          ) : null}
          <div className="dialog-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setConfirming(null)}
              disabled={deliver.isPending}
            >
              تراجع
            </button>
            <button
              className="button button--large"
              type="button"
              onClick={() => deliver.mutate(confirming)}
              disabled={deliver.isPending}
            >
              <PackageCheck aria-hidden="true" />
              {deliver.isPending ? "جارٍ تسليم الدفعة…" : "نعم، تسليم الدفعة كاملة"}
            </button>
          </div>
        </section>
      ) : null}
      {batches.isPending ? (
        <LoadingState />
      ) : batches.isError ? (
        <ErrorState error={batches.error} retry={() => void batches.refetch()} />
      ) : batches.data.length === 0 ? (
        <EmptyState
          title="لا توجد دفعات جاهزة"
          description="أنشئ دفعة وحدد الكمية ليختار النظام المستفيدين."
        />
      ) : (
        <section className="batch-grid" aria-label="الدفعات الجاهزة للتسليم">
          {batches.data.map((batch) => (
            <article className="batch-card delivery-batch-card" key={batch.id}>
              <div className="row-between">
                <div className="batch-icon">
                  <PackageCheck aria-hidden="true" />
                </div>
                <StatusBadge status={batch.status} />
              </div>
              <h2>{batch.batch_number}</h2>
              <p>
                {batch.agent_name} · {batch.scope_name}
              </p>
              <p>{formatDate(batch.scheduled_date)}</p>
              <dl className="inline-details">
                <div>
                  <dt>المطلوب تسليمه</dt>
                  <dd>{formatNumber(batch.remaining)}</dd>
                </div>
                <div>
                  <dt>لم يحضر</dt>
                  <dd>{formatNumber(batch.no_show)}</dd>
                </div>
              </dl>
              <div className="action-row">
                <Link className="button button--secondary" to={`/batches/${batch.id}`}>
                  <Eye aria-hidden="true" /> عرض وطباعة الكشف
                </Link>
                <button className="button" type="button" onClick={() => setConfirming(batch)}>
                  <PackageCheck aria-hidden="true" /> تسليم الدفعة
                </button>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
